export const ENDPOINTS = {
  AUTH: {
    LOGIN: "/auth/login",
    LOGOUT: "/auth/logout",
    REFRESH: "/auth/refresh",
    ACCEPT_INVITE: "/user/accept-invite",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD: "/auth/reset-password",
    VERIFY_2FA: "/auth/login/verify-2fa",
    RESEND_2FA: "/auth/login/resend-2fa",
    LOGIN_AS: (userId: string): string => `/auth/login-as/${userId}`,
  },

  USERS: {
    LIST: "/user",
    DETAIL: (id: string): string => `/user/${id}`,
    CREATE: "/user",
    UPDATE: (id: string): string => `/user/${id}`,
    DELETE: (id: string): string => `/user/${id}`,
    REINVITE: (id: string): string => `/user/${id}/invite`,
    ME: "/users/me",
    EXPORT_CSV: "/user/export-csv",
  },

  PROJECTS: {
    LIST: "/project",
    DETAIL: (id: string): string => `/project/${id}`,
    CREATE: "/project",
    UPDATE: (id: string): string => `/project/${id}`,
    DELETE: (id: string): string => `/project/${id}`,
    AIA_SUMMARY: (id: string): string => `/project/${id}/aia-summary`,
    AIA_BILLING_SUMMARY: (id: string): string =>
      `/project/${id}/aia-billing-summary`,
    AIA_BILLING_SUMMARY_EXPORT: (id: string): string =>
      `/project/${id}/aia-billing-summary/export`,
    MARK_COMPLETE: (id: string): string => `/project/${id}/mark-complete`,
    LENDER_PRECON_ACTION: (id: string): string =>
      `/project/${id}/lender/pre-construction-action`,
    OVERVIEW: (id: string): string => `/project/${id}/overview`,
    SHARE_LINKS: (id: string): string => `/project/${id}/share-links`,
  },

  LENDER: {
    PENDING_DRAW_APPROVAL: (projectId: string): string =>
      `/project/${projectId}/lender/pending-draw-approval`,
    DRAW_ACTION: (projectId: string, drawId: string): string =>
      `/project/${projectId}/lender/draw-action/${drawId}`,
    COMMENTS: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/comments`,
  },

  GC_REQUIREMENTS: {
    LIST: (projectId: string): string =>
      `/project/${projectId}/gc/requirements`,
    VIEW: (projectId: string): string =>
      `/project/${projectId}/gc/requirements/view`,
    SIGN: (projectId: string, publish = false): string =>
      `/project/${projectId}/gc/requirements/sign?publish=${publish}`,
    UPDATE: (projectId: string, reqId: string): string =>
      `/project/${projectId}/gc/requirements/${reqId}`,
  },

  DRAWS: {
    LIST: (projectId: string): string => `/project/${projectId}/draws`,
    DETAIL: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}`,
    CREATE: (projectId: string): string => `/projects/${projectId}/draws`,
    UPDATE: (projectId: string, drawId: string): string =>
      `/projects/${projectId}/draws/${drawId}`,
    SUBMIT: (projectId: string, drawId: string): string =>
      `/projects/${projectId}/draws/${drawId}/submit`,
    APPROVE: (projectId: string, drawId: string): string =>
      `/projects/${projectId}/draws/${drawId}/approve`,
    REJECT: (projectId: string, drawId: string): string =>
      `/projects/${projectId}/draws/${drawId}/reject`,
    CANCEL: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/cancel`,
    ACTIVE_SOV_LIEN_ITEMS: (
      projectId: string,
      page = 1,
      limit = 10,
      q = "",
    ): string =>
      `/project/${projectId}/draws/active-sov-lien-items?page=${page}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
    SAVE: (projectId: string, publish = false, drawId = ""): string =>
      `/project/${projectId}/draws?publish=${publish}${drawId ? `&drawId=${drawId}` : ""}`,
    INVITE_LIEN_WAIVER: (
      projectId: string,
      drawId: string,
      lienWaiverId: string,
    ): string =>
      `/project/${projectId}/draws/${drawId}/lien-waivers/${lienWaiverId}/invite`,
    LIEN_ITEMS: (projectId: string): string =>
      `/project/${projectId}/draws/lien-items`,
    OWNER_SIGNATURE_PENDING: (projectId: string): string =>
      `/project/${projectId}/draws/owner-signature-pending`,
    TIMELINE: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/timeline`,
    INSPECTION: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/inspection`,
    FULL_DETAIL: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/detail`,
    OWNER_SIGN: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/owner-sign`,
  },

  CHANGE_ORDERS: {
    LIST: (projectId: string): string => `/projects/${projectId}/change-orders`,
    DETAIL: (projectId: string, id: string): string =>
      `/projects/${projectId}/change-orders/${id}`,
    CREATE: (projectId: string): string =>
      `/projects/${projectId}/change-orders`,
    UPDATE: (projectId: string, id: string): string =>
      `/projects/${projectId}/change-orders/${id}`,
    APPROVE: (projectId: string, id: string): string =>
      `/projects/${projectId}/change-orders/${id}/approve`,
    REJECT: (projectId: string, id: string): string =>
      `/projects/${projectId}/change-orders/${id}/reject`,
    GC_LIST: (projectId: string): string =>
      `/project/${projectId}/change-orders`,
    GC_GET: (projectId: string, id: string): string =>
      `/project/${projectId}/change-orders/${id}`,
    GC_CREATE: (projectId: string, publish = true): string =>
      `/project/${projectId}/change-orders?publish=${publish}`,
    LENDER_LIST: (projectId: string, status = ""): string =>
      `/project/${projectId}/change-orders${status ? `?status=${status}` : ""}`,
    LENDER_DETAIL: (projectId: string, changeOrderId: string): string =>
      `/project/${projectId}/change-orders/${changeOrderId}`,
    // LENDER_ACTION: (projectId: string, changeOrderId: string): string =>
    //   `/project/${projectId}/change-orders/${changeOrderId}/action`,
    OWNER_ACTION: (projectId: string, changeOrderId: string): string =>
      `/project/${projectId}/change-orders/${changeOrderId}/action`,
  },

  LIEN_WAIVERS: {
    LIST: (projectId: string): string => `/projects/${projectId}/lien-waivers`,
    DETAIL: (projectId: string, id: string): string =>
      `/projects/${projectId}/lien-waivers/${id}`,
    CREATE: (projectId: string): string =>
      `/projects/${projectId}/lien-waivers`,
    SIGN: (projectId: string, id: string): string =>
      `/projects/${projectId}/lien-waivers/${id}/sign`,
  },

  INSPECTIONS: {
    LIST: (projectId: string): string => `/projects/${projectId}/inspections`,
    DETAIL: (projectId: string, id: string): string =>
      `/projects/${projectId}/inspections/${id}`,
    CREATE: (projectId: string): string => `/projects/${projectId}/inspections`,
    UPDATE: (projectId: string, id: string): string =>
      `/projects/${projectId}/inspections/${id}`,
    ASSIGN: (projectId: string, id: string): string =>
      `/projects/${projectId}/inspections/${id}/assign`,
    SUBMIT: (projectId: string, id: string): string =>
      `/projects/${projectId}/inspections/${id}/submit`,
    COMMERCIAL_INSPECTION_FORM: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/inspection/commercial`,
    RESIDENTIAL_INSPECTION_FORM: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/inspection/residential`,
    SAVE: (projectId: string, drawId: string, publish = false): string =>
      `/project/${projectId}/draws/${drawId}/inspection/commercial?publish=${publish}`,
    RESIDENTIAL_SAVE: (
      projectId: string,
      drawId: string,
      publish = false,
    ): string =>
      `/project/${projectId}/draws/${drawId}/inspection/residential?publish=${publish}`,
  },

  PRECON: {
    DETAIL: (projectId: string): string => `/projects/${projectId}/precon`,
    SAVE: (projectId: string): string => `/projects/${projectId}/precon`,
    SUBMIT: (projectId: string): string =>
      `/projects/${projectId}/precon/submit`,
    APPROVE: (projectId: string): string =>
      `/projects/${projectId}/precon/approve`,
    DRAFT: (projectId: string): string =>
      `/project/${projectId}/pre-construction`,
    SAVE_REPORT: (projectId: string, publish = false): string =>
      `/project/${projectId}/pre-construction?publish=${publish}`,
    GC_REVIEW: (projectId: string, isPreConstruction?: boolean): string =>
      `/project/${projectId}/pre-construction/review${isPreConstruction ? "?isPreConstruction=true" : ""}`,
  },

  GC_CHECKLIST: {
    GET: (projectId: string): string => `/project/${projectId}/gc/checklist`,
    SUBMIT: (projectId: string, publish = false): string =>
      `/project/${projectId}/gc/checklist?publish=${publish}`,
    UPLOAD_DOCUMENT: (projectId: string, itemId: string): string =>
      `/project/${projectId}/gc/checklist/${itemId}/document`,
  },

  ADMIN_CHECKLIST: {
    GET: (projectId: string): string =>
      `/project/${projectId}/pre-construction/checklist`,
  },

  GC_BUDGET: {
    SCHEDULE_BUDGET: (projectId: string): string =>
      `/project/${projectId}/gc/schedule-budget`,
  },

  REPORTS: {
    LIST: "/reports",
    DETAIL: (id: string): string => `/reports/${id}`,
    GENERATE: "/reports/generate",
  },

  SUB_CONTRACTORS: {
    LIST: "/sub-contractors",
    UPDATE: (id: string): string => `/sub-contractors/${id}`,
    DELETE: (id: string): string => `/sub-contractors/${id}`,
  },

  NOTIFICATIONS: {
    LIST: "/notifications",
    READ: "/notifications/read",
    READ_ALL: "/notifications/mark-all-read",
    UNREAD_COUNT: "/notifications/unread-count",
    REGISTER_FCM_TOKEN: "/auth/notification",
    SETTINGS: "/user/me/notification-preferences",
    UPDATE_SETTING: "/user/me/notification-preferences",
  },

  DOCUMENTS: {
    LIST: (projectId: string): string => `/project/${projectId}/documents/`,
    DOWNLOAD: (projectId: string, id: string, useOwnerId = false): string =>
      `/project/${projectId}/documents/download?${useOwnerId ? "ownerId" : "id"}=${id}`,
    UPLOAD: (projectId: string): string => `/project/${projectId}/documents`,
  },

  COMPANIES: {
    LIST: "/companies",
    DETAIL: (id: string): string => `/companies/${id}`,
    BRANCHES_LIST: "/companies/branches",
    BRANCH_CREATE: (companyId: string): string =>
      `/companies/${companyId}/branches`,
    BRANCHES: (id: string): string => `/companies/branches?companyId=${id}`,
    BRANCH_DETAIL: (companyId: string, branchId: string): string =>
      `/companies/${companyId}/branches/${branchId}`,
  },

  INSPECTOR_ASSIGNMENT: {
    PROJECTS: "/inspector-assignment/projects",
    CREATE: (projectId: string): string => `/inspector-assignment/${projectId}`,
    REMOVE: (assignmentId: string): string =>
      `/inspector-assignment/${assignmentId}`,
    INSPECTORS: (page = 1, limit = 10): string =>
      `/inspector?page=${page}&limit=${limit}`,
    PROJECT_DETAILS: "/inspector/project-details",
    GET_LEAVES: (inspectorId: string): string =>
      `/inspector/${inspectorId}/leaves`,
    ADD_LEAVE: (inspectorId: string): string =>
      `/inspector/${inspectorId}/leaves`,
    EDIT_LEAVE: (leaveId: string): string => `/inspector/leaves/${leaveId}`,
    DELETE_LEAVE: (leaveId: string): string => `/inspector/leaves/${leaveId}`,
    ASSIGN_VACATION: (inspectorId: string): string =>
      `/inspector/${inspectorId}/vacation`,
    UPDATE_VACATION: (leaveId: string): string =>
      `/inspector/vacation/${leaveId}`,
    VACATION_PROJECTS: (leaveId: string): string =>
      `/inspector/vacation/${leaveId}/projects`,
  },

  UPLOADS: {
    GET_URL: (key: string): string =>
      `/uploads/url?key=${encodeURIComponent(key)}`,
  },

  PROGRESS_REPORTS: {
    LIST: (projectId: string): string =>
      `/project/${projectId}/progress-reports/list`,
    DETAIL: (projectId: string, reportId: string): string =>
      `/project/${projectId}/progress-reports/${reportId}`,
  },

  CONSTRUCTION_REPORT: {
    PROGRESS_REPORT: (projectId: string, drawId: string): string =>
      `/project/${projectId}/draws/${drawId}/progress-report`,
    PUBLISH_REPORT: (
      projectId: string,
      drawId: string,
      publish = false,
    ): string =>
      `/project/${projectId}/draws/${drawId}/progress-report?publish=${publish}`,
  },

  GC_PROGRESS: {
    GET: (projectId: string): string => `/project/${projectId}/gc/progress`,
    UPDATE: (projectId: string, publish = false): string =>
      `/project/${projectId}/gc/progress?publish=${publish}`,
  },

  SOV_SETUP: {
    GET_AIA: (page: number, limit: number): string =>
      `/aia-items?page=${page}&limit=${limit}`,
    CREATE: (projectId: string): string =>
      `/project/${projectId}/gc/sov?publish=true`,
    DRAFT: (projectId: string): string =>
      `/project/${projectId}/gc/sov?publish=false`,
    GET_SOV_DRAFT: (projectId: string): string =>
      `/project/${projectId}/gc/sov/draft`,
    GET_SUBCONTRACTORS: (search = "", page = 1, limit = 20): string =>
      `/sub-contractors?${search ? `q=${search}&` : ""}page=${page}&limit=${limit}`,
    IMPORT_EXCEL: (projectId: string, fileKey: string): string =>
      `/project/${projectId}/gc/sov/import-excel?fileKey=${encodeURIComponent(fileKey)}`,
    GET_SOV_VERSIONS: (projectId: string): string =>
      `/project/${projectId}/gc/sov/versions`,
    GET_SOV_VERSION: (projectId: string, versionId: string): string =>
      `/project/${projectId}/gc/sov/versions/${versionId}`,
    EXPORT_CSV: (projectId: string, versionId: string): string =>
      `/project/${projectId}/gc/sov/versions/${versionId}/lien-items/export-csv`,
  },

  ADMIN: {
    CHANGE_ORDERS: (
      projectId: string,
      status = "",
      page = 1,
      limit = 20,
    ): string =>
      `/admin/change-orders?projectId=${projectId}${status ? `&status=${status}` : ""}&page=${page}&limit=${limit}`,
    CHANGE_ORDERS_EXPORT: (projectId: string, status = ""): string =>
      `/admin/change-orders/export?projectId=${projectId}${status ? `&status=${status}` : ""}`,
  },

  PROFILE: {
    ME: "/auth/me",
    UPDATE: "/user/me",
    CHANGE_PASSWORD: "/user/reset-password",
    ACTIVE_SESSIONS: "/auth/sessions",
    REVOKE_SESSION: (sessionId: string): string =>
      `/auth/sessions/${sessionId}`,
    REVOKE_ALL_SESSIONS: "/auth/logout-all",
    UPLOAD_PHOTO: "/users/me/photo",
    DELETE_PHOTO: "/users/me/photo",
    SEND_PHONE_OTP: "/user/me/phone/send-otp",
    VERIFY_PHONE_OTP: "/user/me/phone/verify-otp",
  },

  MESSAGE_MODULE: {
    CONVERSATIONS: (id: string): string => `/project/${id}/conversations`,
    MESSAGES: (
      projectId: string,
      conversationId: string,
      page = 1,
      limit = 20,
    ): string =>
      `/project/${projectId}/conversations/${conversationId}/messages?page=${page}&limit=${limit}`,
    SEND_MESSAGE: (projectId: string, conversationId: string): string =>
      `/project/${projectId}/conversations/${conversationId}/messages`,
  },
} as const;
